import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import ImageCropper from "./ImageCropper";

const EditCategoryModal = ({
  isOpen,
  toggle,
  onSubmit,
  initialData = null,
}) => {
  const [cropperOpen, setCropperOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: initialData?.name || "",
      image: initialData?.images,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Category name is required"),
      image: Yup.mixed()
        .nullable()
        .test("fileType", "Unsupported file format", (value) => {
          if (!value) return true;
          if (value instanceof File) {
            return [
              "image/jpeg",
              "image/png",
              "image/jpg",
              "image/gif",
            ].includes(value.type);
          }
          return true;
        }),
    }),

    onSubmit: async (values, { resetForm }) => {
      const formData = new FormData();
      formData.append("name", values.name);

      if (values.image instanceof File && values.image.size > 0) {
        formData.append("image", values.image);
      }

      if (initialData?.id) {
        formData.append("id", initialData.id);
      }

      try {
        await onSubmit(formData);
        toggle();
        resetForm();
      } catch (err) {
        console.error("Update failed in modal", err);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
    }
  }, [isOpen]);

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
      setCropperOpen(true);
    }
    // validation.setFieldValue("image", file);
  };

  const handleCropComplete = (croppedBlob) => {
    const croppedFile = new File([croppedBlob], "cropped_image.jpg", {
      type: "image/jpeg",
    });

    validation.setFieldValue("image", croppedFile);
    setCropperOpen(false);
    setPreviewImage(null);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} scrollable centered fade={true}>
      <ModalHeader style={{ background: "#E0F8E0" }} toggle={toggle}>
        {initialData ? "Edit Category" : "Add Category"}
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit} encType="multipart/form-data">
        <ModalBody>
          <FormGroup>
            <Label for="name">Category Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter category name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name}
              invalid={validation.touched.name && !!validation.errors.name}
            />
            {validation.touched.name && validation.errors.name && (
              <FormFeedback>{validation.errors.name}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label for="image">Upload Image</Label>
            <Input
              id="image"
              name="image"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.image && !!validation.errors.image}
            />

            {validation.values.image &&
              typeof validation.values.image === "string" && (
                <div className="mt-5 text-center">
                  <img
                    src={validation.values.image}
                    alt="Current category"
                    className="img-fluid rounded mt-1"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}

            {validation.values.image &&
              validation.values.image instanceof File && (
                <div className="mt-5 text-center">
                  <img
                    src={URL.createObjectURL(validation.values.image)}
                    alt="Preview"
                    className="img-fluid rounded mt-1"
                    style={{ maxHeight: "120px" }}
                  />
                </div>
              )}

            {validation.touched.image && validation.errors.image && (
              <FormFeedback>{validation.errors.image}</FormFeedback>
            )}
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" className="btn-sm" onClick={toggle}>
            Cancel
          </Button>
          <Button color="success" className="btn-sm" type="submit">
            {/* {initialData ? "Update Category" : "Add Category"} */}
            Save Changes
          </Button>
        </ModalFooter>
      </Form>

      {cropperOpen && previewImage && (
        <Modal
          isOpen={cropperOpen}
          toggle={() => setCropperOpen(false)}
          centered
        >
          <ModalHeader toggle={() => setCropperOpen(false)}>
            Crop Image
          </ModalHeader>
          <ModalBody className="text-center mt-3">
            <ImageCropper
              imageSrc={previewImage}
              onCropComplete={handleCropComplete}
              onClose={() => setCropperOpen(false)}
              aspect={16 / 9} 
            />
          </ModalBody>
        </Modal>
      )}
    </Modal>
  );
};

export default EditCategoryModal;

